import { TranslateService } from "@ngx-translate/core";
import {
  UserInvitationResponse,
  UserLoginRegistrationResponse,
} from "./enumLoginRegistrationResponse";
import { AccessControlException } from "./enumAccessControl";
import { HiLevelDataFormat } from "./enumAddress";
import { AreaKind } from "./enumArea";
import { CardState } from "./enumCard";
import { CustomerStaffState, CustomerStaffType } from "./enumCustomerStaff";
import {
  BIGDevice,
  BIGDeviceInputContactType,
  BIGDeviceInputFunction,
  BIGDeviceOperationMode,
  BIGDeviceRelaysQ896S,
  BIGDeviceValueToSend,
  BIGDeviceValueType,
} from "./enumDevice";
import { EnvironmentCategory, EnvironmentType } from "./enumEnvironment";
import { GatewayKind } from "./enumGateway";
import { LogLevel } from "./enumLog";
import { ApplicationTheme, ManufacturerSystem } from "./enumMisc";
import { RegisteredAppLanguage } from "./enumSettings";
import { CardProgrammerKind } from "./enumCardProgrammerKind";

/**
 * Convert generic enum to string
 */
export abstract class EnumStringConverter {
  //#region Utility methods
  /**
   * This method converts a generic enum to a "mirror-array".
     This array contains all specified enum keyes, in the exact order in which they are defined.
  
     Possible uses include to dinamically initialise the "ion-select-option"-elements list in pages/components' html template.
   * @returns 
   */
  public static genericEnumtoArray<T>(enumGeneric): Array<T> {
    let enumArray = new Array<T>();
    for (const [propertyKey, propertyValue] of Object.entries(enumGeneric)) {

      if (Number.isNaN(Number(propertyKey))) {

        enumArray.push(enumGeneric[propertyKey]);
      }
    }


    return enumArray;
  }

  /**
   * This method returns, given a peculiar enum and a value inside of it, the specific key to which that value is associated.
   *
   * THIS IS EXPECIALLY USEFUL WHEN DEALING WITH STRING-ENUMs
   * @param myEnum
   * @param enumValue
   * @returns
   */
  public static getEnumKeyByEnumValue(myEnum, enumValue) {
    let keys = Object.keys(myEnum).filter((x) => myEnum[x] == enumValue);
    return keys.length > 0 ? keys[0] : null;
  }

  //#endregion

  /**
   * Convert generic application theme to string enum
   *
   * @param appTheme Application theme
   * @returns
   */
  public static applicationThemeToStringEnum(
    appTheme: ApplicationTheme
  ): string {
    let appThemeString: string = "";

    switch (appTheme) {
      case ApplicationTheme.BIG:
        appThemeString = "big";
        break;
      case ApplicationTheme.DIGITAL_PLACE:
        appThemeString = "digital-place";
        break;
      case ApplicationTheme.DREX:
        appThemeString = "drex";
        break;
      case ApplicationTheme.BLUMOTIX:
        appThemeString = "blumotix";
        break;
      case ApplicationTheme.SMART_HUB:
        appThemeString = "smart-hub";
        break;
      case ApplicationTheme.VIMAR:
        appThemeString = "vimar";
        break;
      case ApplicationTheme.MDT:
        appThemeString = "mdt";
        break;
    }

    return appThemeString;
  }

  //#region Address
  /**
   * Convert enum hi level data format to string enum
   *
   * @param hiLevelDataFormat Hi level data format
   * @returns
   */
  public static hiLevelDataFormatToStringEnum(
    hiLevelDataFormat: HiLevelDataFormat
  ): string {
    let returnValue: string = "";

    switch (hiLevelDataFormat) {
      case HiLevelDataFormat.BOOLEAN:
        returnValue = "BOOLEAN";
        break;
      case HiLevelDataFormat.BYTE_ARRAY:
        returnValue = "BYTE_ARRAY";
        break;
      case HiLevelDataFormat.CHAR:
        returnValue = "CHAR";
        break;
      case HiLevelDataFormat.DATE:
        returnValue = "DATE";
        break;
      case HiLevelDataFormat.DATETIME:
        returnValue = "DATE_TIME";
        break;
      case HiLevelDataFormat.INTEGER:
        returnValue = "INTEGER";
        break;
      case HiLevelDataFormat.FLOAT:
        returnValue = "FLOAT";
        break;
      case HiLevelDataFormat.TIME:
        returnValue = "TIME";
        break;
      case HiLevelDataFormat.UNKNOWN:
        returnValue = "UNKNOWN";
        break;
      case HiLevelDataFormat.XML:
        returnValue = "XML";
        break;
      default:
        returnValue = "STRING";
        break;
    }

    return returnValue;
  }
  //#endregion

  //#region Area
  /**
   * Get area kind string enum value
   *
   * @param areaKind Area kind to convert
   * @param translateService Translate service to perform translation
   * @returns
   */
  public static areaKindToStringEnum(
    areaKind: AreaKind,
    translateService: TranslateService
  ): string {
    let areaKindString: string = "";

    switch (areaKind) {
      case AreaKind.DEFAULT:
        areaKindString = translateService.instant("dictionary.default");
        break;
      case AreaKind.ACCESS_CONTROL_GROUP_OF_ENVIRONMENT:
        areaKindString = translateService.instant(
          "dictionary.groupOfEnvironment"
        );
        break;
    }

    return areaKindString;
  }
  //#endregion

  //#region Log
  /**
   *  Convert generic log level to string enum value
   *
   * @param logLevel Log level effective value
   */
  public static logLevelAsStringEnum(
    logLevel: LogLevel,
    translateService: TranslateService
  ): string {
    let returnValue: string = "";

    switch (logLevel) {
      case LogLevel.LOG_ALL:
        returnValue = translateService.instant("dictionary.always");
        break;
      case LogLevel.LOG_INFO:
        returnValue = translateService.instant("dictionary.info");
        break;
      case LogLevel.LOG_WARNING:
        returnValue = translateService.instant("dictionary.warnings");
        break;
      case LogLevel.LOG_ERROR:
        returnValue = translateService.instant("dictionary.errors");
        break;
      case LogLevel.LOG_NONE:
        returnValue = translateService.instant("dictionary.never");
        break;
      default:
        break;
    }

    return returnValue;
  }
  //#endregion

  /**
   * Get access control exception string enum value
   *
   * @param accessControlExcetpion Access control exception
   * @param translateService Translatee service to perform translation
   * @returns
   */
  public static accessControlExceptionToStringEnum(
    accessControlException: AccessControlException,
    translateService: TranslateService
  ): string {
    let accessControlExceptionString: string = "";
    //
    switch (accessControlException) {
      case AccessControlException.ENVIRONMENT_WITHOUT_ACCESS_CONTROL_SYSTEM:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionNoAccesses"
        );
        break;
      case AccessControlException.UPDATE_KEY_WITH_CARD_PROGRAMMER:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCardToBeUpdated"
        );
        break;
      case AccessControlException.CUSTOMER_ROOM_LIMIT_REACHED:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCustomerMaximumAccessLimit"
        );
        break;
      case AccessControlException.STAFF_ROOM_ACCESS_LIMIT_REACHED:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionStaffMaximumAccessLimit"
        );
        break;
      case AccessControlException.SAFLOCK_ROOM_ACCESS_LIMIT_REACHED:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionRoomMaximumLimit"
        );
        break;
      case AccessControlException.SAFLOCK_COMMON_AREA_LIMIT_REACHED:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCommonAreaMaximumLimit"
        );
        break;
      case AccessControlException.SAFLOCK_ONLY_ROOM_AND_ACCESS_COMMON:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCommonAreaRoomExclusive"
        );
        break;
      case AccessControlException.INCOMPATIBLE_ACCESS_CONTROL_SYSTEM:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionIncompatibleControlSystem"
        );
        break;
      case AccessControlException.ROOM_NOT_FOUND:
        accessControlExceptionString = translateService.instant(
          "dictionary.noEnvironmentRoom"
        );
        break;
      case AccessControlException.RESERVATION_NOT_FOUND:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionNoBookingFound"
        );
        break;
      case AccessControlException.CARD_IS_NOT_VALID:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionNotValidCard"
        );
        break;
      case AccessControlException.CARD_STATE_NOT_VALID:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionNotValidStatusCard"
        );
        break;
      case AccessControlException.CARD_IS_NOT_TEMPLATE:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionNotTemplateCard"
        );
        break;
      case AccessControlException.CARD_NOT_FOUND:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCardNotFound"
        );
        break;
      case AccessControlException.CARD_OPERATION_GENERIC_EXCEPTION:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCardGeneral"
        );

        break;
      case AccessControlException.CARD_MANUFACTURER_SYSTEM_NOT_ASSIGNED:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionManufacturerSystemNotAssigned"
        );
        break;
      case AccessControlException.CARD_CHANGE_CODE_NEED_REWRITE:
        accessControlExceptionString = translateService.instant(
          "dictionary.rewriteCard"
        );
        break;
      case AccessControlException.CARD_CHANGE_CODE_ERROR_NOT_REAL_CARD:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionNotRealCard"
        );
        break;
      case AccessControlException.CARD_CHANGE_CODE_ERROR_OUT_OF_RANGE:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCodeOutOfrange"
        );
        break;
      case AccessControlException.CARD_CHANGE_CODE_ERROR_CARD_NUMBER_ALREADY_USED:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCodeAlreadyInUse"
        );
        break;
      case AccessControlException.CARD_CHANGE_CODE_ERROR_BLUEMOTIX5:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCodeBluemotix5"
        );
        break;
      case AccessControlException.CARD_CHANGE_CODE_ERROR_GENERIC:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCodeGeneralError"
        );
        break;
      case AccessControlException.CARD_ENVIRONMENT_NOT_EXIST:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCardEnvironmentInexistent"
        );
        break;
      case AccessControlException.CARD_ENVIRONMENT_GENERIC_OPERATION_EXCEPTION:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCardEnvironmentGeneral"
        );
        break;
      case AccessControlException.CUSTOMER_STAFF_NOT_FOUND:
        accessControlExceptionString = translateService.instant(
          "dictionary.noCustomerStaff"
        );
        break;
      case AccessControlException.CUSTOMER_STAFF_SURNAME_NOT_VALUD:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCustomerStaffSurnameNotValid"
        );
        break;
      case AccessControlException.CUSTOMER_STAFF_NOT_CREATED:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCustomerStaffInexistent"
        );
        break;
      case AccessControlException.CUSTOMER_STAFF_MAIL_ALREADY_EXIST:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCustomerStaffMailExist"
        );
        break;
      case AccessControlException.ENVIRONMENT_ID_NOT_VALID:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionAddressNotValid"
        );
        break;
      case AccessControlException.ENVIRONMENT_NOT_FOUND:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionEnvironmentAddressNotFound"
        );
        break;
      case AccessControlException.CARD_UNLOCK_GENERIC_EXCEPTION:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCardUnlockGeneric"
        );
        break;
      case AccessControlException.ENVIRONMENT_MANUFACTURER_SYSTEM_NOT_VALID:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionEnvironmentManufacturerSystemNotValid"
        );
        break;
      case AccessControlException.CISA_CATEGORY_NOT_VALID:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCisaCategoryNotValid"
        );
        break;
      case AccessControlException.CISA_ZONE_NOT_VALID:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCisaZoneNotValid"
        );
        break;
      case AccessControlException.CISA_ZONE_NOT_ALLOWED_ON_GUEST_CARD:
        accessControlExceptionString = translateService.instant(
          "dictionary.accessControlExceptionCisaZoneNotAllowedOnGuestCard"
        );
        break;
      default:
        accessControlExceptionString = translateService.instant(
          "dictionary.genericErrorProcess"
        );
        break;
    }

    return accessControlExceptionString;
  }

  //#region Gateway
  /**
   * Convert generic gateyway kind to string enum value
   *
   * @param gatewayKind Gateway kind value
   */
  public static gatewayKindToStringEnum(gatewayKind: GatewayKind): string {
    let gatewayKindString: string = "";

    switch (gatewayKind) {
      case GatewayKind.ACCESS_CONTROL:
        gatewayKindString = "Access Control";
        break;
      default:
        const lowerCaseCrudeName: string =
          GatewayKind[gatewayKind]?.toLowerCase();

        if (lowerCaseCrudeName) {
          const lowerCaseFriendlyName: string = lowerCaseCrudeName.replace(
            new RegExp("_", "gi"),
            " "
          );
          gatewayKindString = lowerCaseFriendlyName;
        } else {
          gatewayKindString = lowerCaseCrudeName;
        }

        break;
    }

    return gatewayKindString;
  }
  //#endregion

  //#region Customer/Staff

  /**
   * This method handle both the association between each "CustomerStaffType" enum's value and a user-friendly string AND the localisation of these strings.
   * @param staffType The enum value to be associated with a user-friendly, localised string
   * @param translateService Ref. to TranslateService, handling the string localistaion.
   * @returns A user-friendly, localised string.
   */
  public static CustomerStaffTypeAsStringEnum(
    staffType: CustomerStaffType,
    translateService: TranslateService
  ): string {
    let returnValue: string = "";
    switch (staffType) {
      case CustomerStaffType.CUSTOMER:
        returnValue = translateService.instant("dictionary.customer");
        break;
      case CustomerStaffType.STAFF:
        returnValue = translateService.instant("dictionary.staff");
        break;
      case CustomerStaffType.GROUP_CUSTOMERS:
        returnValue = translateService.instant("dictionary.groupCustomers");
        break;
      case CustomerStaffType.GROUP_STAFF:
        returnValue = translateService.instant("dictionary.groupStaff");
        break;
      default:
        returnValue = translateService.instant("dictionary.unknow");
        break;
    }

    return returnValue;
  }

  //#endregion

  //#region Account
  /**
   *This method handle both the association between each "UserInvitationResponse" enum's value and a user-friendly string AND the localisation of these strings.
   * @param loginRegistrationResponse The enum value to be associated with a user-friendly, localised string
   * @param translateService Ref. to TranslateService, handling the string localistaion.
   * @returns A user-friendly, localised string.
   */
  public static UserInvitationResponseAsStringEnum(
    response: UserInvitationResponse,
    translateService: TranslateService
  ) {
    let returnValue: string = "";

    switch (response) {
      case UserInvitationResponse.INVITATION_NOT_FOUND:
        returnValue = translateService.instant("dictionary.invitationNotFound");
        break;
      case UserInvitationResponse.USER_NOT_ACTIVE:
        returnValue = translateService.instant("dictionary.userNotActive");
        break;
      case UserInvitationResponse.USER_NOT_REGISTRERED:
        returnValue = translateService.instant(
          "dictionary.needToCreateAnAccount"
        );
        break;
      case UserInvitationResponse.USER_TOKEN_EXPIRED:
        returnValue = translateService.instant("dictionary.userTokenExpired");
        break;
      case UserInvitationResponse.USER_ALREADY_ASSOCIATED:
        returnValue = translateService.instant(
          "dictionary.userAlreadyAssociated"
        );
        break;

      case UserInvitationResponse.GENERIC_ERROR:
      default:
        returnValue = "";
        //translateService.instant(          "dictionary.invitationGenericError"        );
        break;
    }

    return returnValue;
  }
  /**
   * This method handle both the association between each "UserLoginRegistrationResponse" enum's value and a user-friendly string AND the localisation of these strings.
   * @param loginRegistrationResponse The enum value to be associated with a user-friendly, localised string
   * @param translateService Ref. to TranslateService, handling the string localistaion.
   * @returns A user-friendly, localised string.
   */
  public static UserLoginRegistrationResponseAsStringEnum(
    loginRegistrationResponse: UserLoginRegistrationResponse,

    translateService: TranslateService
  ): string {
    let returnValue: string = "";
    switch (loginRegistrationResponse) {
      case UserLoginRegistrationResponse.NO_EXCEPTION:
        //returnValue = translateService.instant("dictionary.customer");DPUserRegistrationSuccess1
        returnValue = translateService.instant(
          "dictionary.DPUserRegistrationSuccess1"
        );
        break;
      case UserLoginRegistrationResponse.USER_ALREADY_EXISTS:
        returnValue = translateService.instant(
          "dictionary.userAlreadyPresentInPlatform"
        );
        break;
      case UserLoginRegistrationResponse.USER_ALREADY_ACTIVE:
        returnValue = translateService.instant("dictionary.userAlreadyActive");
        break;
      case UserLoginRegistrationResponse.USER_LOCKED:
        returnValue = translateService.instant("dictionary.userLocked");
        break;
      case UserLoginRegistrationResponse.USER_NOT_FOUND:
        returnValue = translateService.instant("dictionary.userNotFound");
        break;
      case UserLoginRegistrationResponse.USER_ACTIVATION_EXPIRED:
        returnValue = translateService.instant(
          "dictionary.userActivationExpired"
        );
        break;
      case UserLoginRegistrationResponse.USER_NOT_ACTIVE:
        returnValue = translateService.instant(
          "dictionary.registeredButNotActive"
        );
        break;
      case UserLoginRegistrationResponse.USER_TAX_CODE_DUPLICATED:
        returnValue = translateService.instant("dictionary.TaxCodeDuplicated");
        break;
      case UserLoginRegistrationResponse.USER_WITH_SAME_VAT:
        returnValue = translateService.instant("dictionary.VATDuplicated");
        break;
      case UserLoginRegistrationResponse.GENERIC_ERROR:
      default:
        returnValue = translateService.instant(
          "dictionary.genericErrorProcess"
        );
        break;
    }

    return returnValue;
  }

  //#endregion

  //#region Device
  /**
   * Convert string device model as enum value
   *
   * @param model
   */
  public static deviceModelEnumAsString(model: string): BIGDevice {
    let returnValue: BIGDevice = BIGDevice.Unknown;

    // Normalize the case: some devices report their model with different casing
    // (e.g. the Q7S reports Build.MODEL as "Q7s"), while these cases are uppercase.
    switch (model?.toUpperCase()) {
      case "Q896S":
        returnValue = BIGDevice.Q896S;
        break;
      case "Q898X":
        returnValue = BIGDevice.Q898X;
        break;
      case "Q8919":
        returnValue = BIGDevice.Q8919;
        break;
      case "Q5":
        returnValue = BIGDevice.Q5;
        break;
      case "Q7S":
        returnValue = BIGDevice.Q7S;
        break;
      case "Q133S":
        returnValue = BIGDevice.Q133S;
        break;
      default:
        returnValue = BIGDevice.Unknown;
        break;
    }

    return returnValue;
  }

  /**
   * This method handle both the association between each "BIGDeviceOperationMode" enum's value and a user-friendly string AND the localisation of these strings.
   * @param operationMode The enum value to be associated with a user-friendly, localised string
   * @param translateService Ref. to TranslateService, handling the string localistaion.
   * @returns A user-friendly, localised string.
   */
  public static operationModeEnumAsString(
    operationMode: BIGDeviceOperationMode,
    translateService: TranslateService
  ): string {
    let returnValue: string = "";
    switch (operationMode) {
      case BIGDeviceOperationMode.STANDARD:
        returnValue = translateService.instant("dictionary.standard");

        break;
      case BIGDeviceOperationMode.ACCESS_CONTROL_MODE:
        returnValue = translateService.instant("dictionary.accessControlMode");
        break;

      case BIGDeviceOperationMode.DIGITAL_PLACE_MODE:
        returnValue = translateService.instant("dictionary.digitalPlaceMode");
        break;

      default:
        returnValue = translateService.instant("dictionary.unknow");
        break;
    }

    return returnValue;
  }

  /**
   * This method handle both the association between each "CustomerStaffType" enum's value and a user-friendly string AND the localisation of these strings.
   * @param defaultRelay The enum value to be associated with a user-friendly, localised string
   * @param translateService Ref. to TranslateService, handling the string localistaion.
   * @returns A user-friendly, localised string.
   */
  public static defaultArrayEnumAsString(
    defaultRelay: BIGDeviceRelaysQ896S,
    translateService: TranslateService
  ): string {
    let returnValue: string = "";
    switch (defaultRelay) {
      case BIGDeviceRelaysQ896S.NO_RELAY:
        returnValue = translateService.instant("dictionary.notDefined");

        break;

      case BIGDeviceRelaysQ896S.RELAY_1:
        returnValue = translateService.instant("dictionary.relay1");

        break;
      case BIGDeviceRelaysQ896S.RELAY_2:
        returnValue = translateService.instant("dictionary.relay2");
        break;

      default:
        returnValue = translateService.instant("dictionary.unknow");
        break;
    }

    return returnValue;
  }

  /**
   * This method converts the "BIGDeviceOperationMode" enum to a "mirror-array".
   * This array contains all "BIGDeviceOperationMode" enum keyes, in the exact order in which they are defined.
   *
   * Possible uses include to dinamically initialise the "ion-select-option"-elements list in pages/components' html template.
   * @returns
   */
  /*
  public static operationModeEnumtoArray(): Array<BIGDeviceOperationMode> {
    let operationModeArray = new Array<BIGDeviceOperationMode>();
    for (const [propertyKey, propertyValue] of Object.entries(BIGDeviceOperationMode)) {
      if (Number.isNaN(Number(propertyKey))) {
        operationModeArray.push(BIGDeviceOperationMode[propertyKey]);
      }
    }

    return operationModeArray;
  }
  */

  /**
   * Convert generic big device modelto string enum
   *
   * @param bigDeviceModel Device model to cast
   * @returns
   */
  public static bigDeviceModelEnumAsString(bigDeviceModel: BIGDevice): string {
    let returnValue: string = "";

    switch (bigDeviceModel) {
      case BIGDevice.Q5:
        returnValue = 'My.To 5"';
        break;
      case BIGDevice.Q896S:
      case BIGDevice.Q7S:
        returnValue = 'My.To 7"';
        break;
      case BIGDevice.Q898X:
      case BIGDevice.Q8919:
      case BIGDevice.Q133S:
        returnValue = 'My.To 10"';
        break;
      default:
        returnValue = "";
        break;
    }

    return returnValue;
  }

  /**
   * Convert device type function number to string enum value
   *
   * @param translateService Translate service to handle instant translation
   * @param inputFunction Input function
   * @returns
   */
  public static inputFunctionEnumAsString(
    translateService: TranslateService,
    inputFunction: number
  ): string {
    let returnValue: string = "";

    switch (inputFunction) {
      case BIGDeviceInputFunction.EDGE:
        returnValue = translateService.instant("dictionary.edge");
        break;
      default:
        returnValue = "";
        break;
    }

    return returnValue;
  }

  /**
   * COnvert input contact type as string enum
   *
   * @param translateService Translate service to handle instant translation
   * @param inputType Input type to handle
   * @returns
   */
  public static inputTypeEnumAsString(
    translateService: TranslateService,
    inputType: number
  ): string {
    let returnValue: string = "";

    switch (inputType) {
      case BIGDeviceInputContactType.BREAK_CONTACT:
        returnValue = translateService.instant("dictionary.breakContact");
        break;
      case BIGDeviceInputContactType.MAKE_CONTACT:
        returnValue = translateService.instant("dictionary.makeContact");
        break;
      default:
        returnValue = "";
        break;
    }

    return returnValue;
  }

  /**
   * Convert value type to string enum
   *
   * @param translateService Translate service to handle instant translation
   * @param valueType Value type to convert
   * @returns
   */
  public static valueTypeEnumAsString(
    translateService: TranslateService,
    valueType: number
  ): string {
    let returnValue: string = "";

    switch (valueType) {
      case BIGDeviceValueType.ON_OFF:
        returnValue = translateService.instant("dictionary.onOff");
        break;
      case BIGDeviceValueType.NUMBER_VALUE:
        returnValue = translateService.instant("dictionary.valueNumber");
        break;
      default:
        returnValue = "";
        break;
    }

    return returnValue;
  }

  /**
   * Hanadle value to sent type as string enum
   *
   * @param translateService Translate service to handle instant translation
   * @param valueToSendType Value to send convert
   * @returns
   */
  public static valueToSendTypeAsStringEnum(
    translateService: TranslateService,
    valueToSendType: number
  ): string {
    let returnValue: string = "";

    switch (valueToSendType) {
      case BIGDeviceValueToSend.NONE:
        returnValue = translateService.instant("dictionary.noAction");
        break;
      case BIGDeviceValueToSend.TOGGLE:
        returnValue = translateService.instant("dictionary.toggle");
        break;
      case BIGDeviceValueToSend.SEND_0:
        returnValue = translateService.instant("dictionary.send0");
        break;
      case BIGDeviceValueToSend.SEND_1:
        returnValue = translateService.instant("dictionary.send1");
        break;
      case BIGDeviceValueToSend.SEND_VALUE:
        returnValue = translateService.instant("dictionary.sendValue");
        break;
      default:
        returnValue = "";
        break;
    }

    return returnValue;
  }

  //#endregion

  //#region Card
  /**
   * Convert generic card state to string enum value
   *
   * @param customerStaffState Input customer/staff state to convert
   * @param translateService  Translate service instance
   * @returns string customer/staff state
   */
  public static customerStaffStateToStringEnum(
    customerStaffState: CustomerStaffState,
    translateService: TranslateService
  ): string {
    let customerStateString: string = "";
    switch (customerStaffState) {
      case CustomerStaffState.ACTIVE:
        customerStateString = translateService.instant("dictionary.active");
        break;
      case CustomerStaffState.ARCHIVED:
        customerStateString = translateService.instant(
          "dictionary.archivedState"
        );
        break;
      case CustomerStaffState.AUTHORIZED:
        customerStateString = translateService.instant("dictionary.authorized");
        break;
      case CustomerStaffState.BLOCKED:
        customerStateString = translateService.instant("dictionary.blocked");
        break;
      case CustomerStaffState.BOOKED:
        customerStateString = translateService.instant("dictionary.booked");
        break;
      case CustomerStaffState.CHECK_IN:
        customerStateString = translateService.instant("dictionary.checkin");
        break;
      case CustomerStaffState.INCOMING:
        customerStateString = translateService.instant("dictionary.incoming");
        break;
      case CustomerStaffState.NOT_ARRIVED:
        customerStateString = translateService.instant("dictionary.notArrived");
        break;
      case CustomerStaffState.OUTCOMING:
        customerStateString = translateService.instant("dictionary.outcoming");
        break;
      case CustomerStaffState.OVERTIME:
        customerStateString = translateService.instant("dictionary.overtime");
        break;
      case CustomerStaffState.UNKNOWN:
        customerStateString = translateService.instant("dictionary.unknow");
        break;
      default:
        customerStateString = translateService.instant("notHandle");
        break;
    }
    return customerStateString;
  }
  //#endregion

  //#region Card
  /**
   * Convert generic card state to string enum value
   *
   * @param cardState Input card state to convert
   * @param translateService  Translate service instance
   * @param busWritten Bus written flag card value
   * @returns string card state
   */
  public static cardStateToStringEnum(
    cardState: CardState,
    translateService: TranslateService,
    busWritten: boolean
  ): string {
    let cardStateString: string = "";

    switch (cardState) {
      case CardState.NOT_REGISTERED:
        cardStateString = translateService.instant("dictionary.notRegistered");
        break;
      case CardState.REGISTERED:
        cardStateString = translateService.instant("dictionary.registered");

        if (busWritten) {
          cardStateString = translateService.instant(
            "dictionary.registeredActive"
          );
        }
        break;
      case CardState.BLOCKED_NOT_REGISTERED:
        cardStateString = translateService.instant(
          "dictionary.blockedNotRegistered"
        );
        break;
      case CardState.BLOCKED_REGISTERED:
        cardStateString = translateService.instant(
          "dictionary.blockedRegistered"
        );
        break;
      case CardState.UNKNOWN:
        cardStateString = translateService.instant("dictionary.unknow");
        break;
      default:
        cardStateString = translateService.instant("notHandle");
        break;
    }

    return cardStateString;
  }
  //#endregion
  a;
  //#region Cards-related (card-readers manufacturer systems and card-programmer manufacturers )
  /**
   * Get card-readers manufacture system string enum
   *
   * @param manufacturerSystem Manufacturer system enum item
   * @returns
   */
  public static manufacturerSystemNameToStringEnum(
    manufacturerSystem: ManufacturerSystem,
    translateService: TranslateService
  ): string {
    let cardManufacturerString: string = "";

    switch (manufacturerSystem) {
      case ManufacturerSystem.ABB:
        cardManufacturerString = "ABB";
        break;
      case ManufacturerSystem.ABB_MAC2:
        cardManufacturerString = "ABB MAC2";
        break;
      case ManufacturerSystem.ABB_MILLENIUM:
        cardManufacturerString = "ABB Millenium";
        break;
      case ManufacturerSystem.ABB_TACTEO:
        cardManufacturerString = "ABB Tacteo";
        break;
      case ManufacturerSystem.API:
        cardManufacturerString = "API";
        break;
      case ManufacturerSystem.AXIS:
        cardManufacturerString = "Axis";
        break;
      case ManufacturerSystem.BACNET:
        cardManufacturerString = "BACNET";
        break;
      case ManufacturerSystem.BECKHOFF:
        cardManufacturerString = "Beckhoff";
        break;
      case ManufacturerSystem.BIG:
      case ManufacturerSystem.BIG_VIRTUAL:
        cardManufacturerString = "BIG";
        break;
      case ManufacturerSystem.BLUEWAVE:
        cardManufacturerString = "BLUEWAVE";
        break;
      case ManufacturerSystem.BLUMOTIX_10:
        cardManufacturerString = "Blumotix 10";
        break;
      case ManufacturerSystem.BLUMOTIX_5:
        cardManufacturerString = "Blumotix 5";
        break;
      case ManufacturerSystem.BLUMOTIX_2023:
        cardManufacturerString = "Blumotix 2023";
        break;
      case ManufacturerSystem.BLUMOTIX_2025_KEYPAD:
        cardManufacturerString = "Blumotix 2025 Keypad";
        break;
      case ManufacturerSystem.BLUMOTIX_2025_MIFARE:
        cardManufacturerString = "Blumotix 2025 Mifare";
        break;
      case ManufacturerSystem.CIE:
        cardManufacturerString = "CIE";
        break;
      case ManufacturerSystem.CISA:
        cardManufacturerString = "CISA";
        break;
      case ManufacturerSystem.DAHUA:
      case ManufacturerSystem.DAHUA_CARD_READER:
        cardManufacturerString = "Dahua";
        break;
      case ManufacturerSystem.DORMAKABASAFLOKSYSTEM6000:
        cardManufacturerString = "Dorma Kaba Saflock System 6000";
        break;
      case ManufacturerSystem.EELECTRON:
        cardManufacturerString = "eElectron";
        break;
      case ManufacturerSystem.EELECTRON_KEYPAD:
        cardManufacturerString = "eElectron keypad";
        break;
      case ManufacturerSystem.EELECTRON_MIFARE:
        cardManufacturerString = "eElectron (Mifare)";
        break;
      case ManufacturerSystem.ESPA:
        cardManufacturerString = "Espa";
        break;
      case ManufacturerSystem.GENERIC_KEYPAD:
        cardManufacturerString = "Generic Keypad";
        break;
      case ManufacturerSystem.GEWISS:
        cardManufacturerString = "Gewiss";
        break;
      case ManufacturerSystem.INTESIS:
        cardManufacturerString = "Intesis";
        break;
      case ManufacturerSystem.LORA:
        cardManufacturerString = "LoRa";
        break;
      case ManufacturerSystem.MBUS:
        cardManufacturerString = "MBus";
        break;
      case ManufacturerSystem.MODBUS:
        cardManufacturerString = "Modbus";
        break;
      case ManufacturerSystem.MIKROTIK:
        cardManufacturerString = "Mikrotik";
        break;
      case ManufacturerSystem.MQTT:
        cardManufacturerString = "MQTT";
        break;
      case ManufacturerSystem.SALTO_IS:
        cardManufacturerString = "Salto IS";
        break;
      case ManufacturerSystem.SALTO_KS:
        cardManufacturerString = "Salto KS";
        break;
      case ManufacturerSystem.SCHNEIDER:
        cardManufacturerString = "Schneider";
        break;
      case ManufacturerSystem.SIEMENS:
        cardManufacturerString = "Siemens";
        break;
      case ManufacturerSystem.VIMAR_EELECTRON:
        cardManufacturerString = "Vimar eElectron";
        break;
      case ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS:
        cardManufacturerString = "Vimar Well Contact Plus";
        break;
      case ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS_2026:
        cardManufacturerString = translateService.instant("dictionary.vimarKnxAccessControl");
        break;
      case ManufacturerSystem.BIG_VIRTUAL_MIFARE:
        cardManufacturerString = "Mifare";
        break;

      case ManufacturerSystem.AKUVOX_KEYPAD:
      case ManufacturerSystem.AKUVOX_CARD_READER:
        cardManufacturerString = "Akuvox";
        break;

      case ManufacturerSystem.ANDROID:
        cardManufacturerString = "Android";
        break;
      case ManufacturerSystem.IOS:
        cardManufacturerString = "iOS";
        break;
      default:
        cardManufacturerString = translateService.instant("dictionary.unknow");
        break;
    }

    return cardManufacturerString;
  }

  /**
   * Get card-programmer manufacturers (kind) string enum
   * @param cardProgrammerKind Card-Programmer Kind enum item
   * @param translateService
   * @returns
   */
  public static cardProgrammerKindNameToStringEnum(
    cardProgrammerKind: CardProgrammerKind,
    translateService: TranslateService
  ): string {
    let cardProgrammerKindString: string = "";

    switch (cardProgrammerKind) {
      case CardProgrammerKind.EELECTRON:
        cardProgrammerKindString = "eElectron";
        break;

      case CardProgrammerKind.GEWISS:
        cardProgrammerKindString = "Gewiss";
        break;

      case CardProgrammerKind.SCHNEIDER:
        cardProgrammerKindString = "Schneider";
        break;

      case CardProgrammerKind.SIEMENS:
        cardProgrammerKindString = "Siemens";
        break;

      case CardProgrammerKind.VIMAR_EELECTRON:
        cardProgrammerKindString = "Vimar eElectron";
        break;

      case CardProgrammerKind.VIMAR_WELLCONTACT_PLUS:
        cardProgrammerKindString = "Vimar WellContact Plus";
        break;

      case CardProgrammerKind.SAFLOCK_SALTOSYSTEM:
        cardProgrammerKindString = "Saflock - Salto System";
        break;

      case CardProgrammerKind.SALTO_IS:
        cardProgrammerKindString = "Salto IS";
        break;

      case CardProgrammerKind.ABB:
        cardProgrammerKindString = "ABB";
        break;

      case CardProgrammerKind.ABB_MILLENIUM_BUS:
        cardProgrammerKindString = "ABB Millenium Bus";
        break;

      case CardProgrammerKind.ABB_MILLENIUM_USB:
        cardProgrammerKindString = "ABB Millenium USB";
        break;

      case CardProgrammerKind.ABB_TACTEO:
        cardProgrammerKindString = "ABB Tacteo";
        break;

      case CardProgrammerKind.DAHUA:
        cardProgrammerKindString = "Dahua";
        break;

      case CardProgrammerKind.UID_READER:
        cardProgrammerKindString = "UID Reader";
        break;

      case CardProgrammerKind.CIE_READER:
        cardProgrammerKindString = "CIE Reader";
        break;

      case CardProgrammerKind.EELECTRON_MIFARE:
        cardProgrammerKindString = "eElectron (Mifare)";
        break;

      case CardProgrammerKind.CISA:
        cardProgrammerKindString = "CISA";
        break;

      case CardProgrammerKind.IOS:
        cardProgrammerKindString = "iOS";
        break;

      case CardProgrammerKind.ANDROID:
        cardProgrammerKindString = "Android";
        break;

      case CardProgrammerKind.MYTO:
        cardProgrammerKindString = "MyTo";
        break;

      case CardProgrammerKind.VIMAR_WELLCONTACT_PLUS_NEW_DESFIRE:
        cardProgrammerKindString = "Vimar tipo 01599 (Desfire)";
        break;

      case CardProgrammerKind.VIMAR_WELLCONTACT_PLUS_NEW_MIFARE:
        cardProgrammerKindString = "Vimar tipo 01599 (Mifare)";
        break;

      case CardProgrammerKind.BIG_MYTO_MIFARE:
        cardProgrammerKindString = "BIG MyTo";
        break;

      case CardProgrammerKind.BLUMOTIX_2025_MIFARE:
        cardProgrammerKindString = "Blumotix 2025 Mifare";
        break;

      default:
        cardProgrammerKindString =
          translateService.instant("dictionary.unknow");
        break;
    }

    return cardProgrammerKindString;
  }

  //#endregion

  //#region Environment
  /**
   * Return enviuronment type as string enum
   *
   * @param environmentType Environemnt type number enum
   * @param translateService translated service to translate localized string
   * @param pluralString Follow string plural string
   * @returns
   */
  public static environmentTypeToStringEnum(
    environmentType: EnvironmentType,
    translateService: TranslateService,
    pluralString: boolean
  ): string {
    let environmentTypeString: string = "";

    switch (environmentType) {
      case EnvironmentType.ROOM:
        environmentTypeString = pluralString
          ? translateService.instant("dictionary.rooms")
          : translateService.instant("dictionary.room");
        break;
      case EnvironmentType.COMMON_AREA:
        environmentTypeString = pluralString
          ? translateService.instant("dictionary.commonZones")
          : translateService.instant("dictionary.commonArea");
        break;
      case EnvironmentType.CUSTOMIZED:
        environmentTypeString = pluralString
          ? translateService.instant("dictionary.cabinets")
          : translateService.instant("dictionary.cabinet");
        break;
      case EnvironmentType.INVISIBLE:
        environmentTypeString = pluralString
          ? translateService.instant("dictionary.invisibles")
          : translateService.instant("dictionary.invisible");
        break;
      case EnvironmentType.SUITE:
        environmentTypeString = pluralString
          ? translateService.instant("dictionary.suites")
          : translateService.instant("dictionary.suite");
        break;
      case EnvironmentType.TECHNICAL_AREA:
        environmentTypeString = pluralString
          ? translateService.instant("dictionary.technicalAreas")
          : translateService.instant("dictionary.technicalArea");
        break;
    }

    return environmentTypeString;
  }

  /**
   * Return enviuronment CATEGORY as string enum
   *
   * @param environmentCategory Environemnt category number enum
   * @param translateService translated service to translate localized string
   * @param pluralString Follow string plural string
   * @returns
   */
  public static environmentCategoryToStringEnum(
    environmentCategory: EnvironmentCategory,
    translateService: TranslateService,
    pluralString: boolean
  ): string {
    let environmentCategoryString: string = "";

    switch (environmentCategory) {
      case EnvironmentCategory.ROOM:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.rooms")
          : translateService.instant("dictionary.room");
        break;
      case EnvironmentCategory.SUITE:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.suites")
          : translateService.instant("dictionary.suite");
        break;
      case EnvironmentCategory.JUNIOR_SUITE:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.juniorSuites")
          : translateService.instant("dictionary.juniorSuite");
        break;
      case EnvironmentCategory.SINGLE_ROOM:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.roomsSingle")
          : translateService.instant("dictionary.roomSingle");
        break;
      case EnvironmentCategory.DOUBLE_ROOM:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.roomsDouble")
          : translateService.instant("dictionary.roomDouble");
        break;
      case EnvironmentCategory.TRIPLE_ROOM:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.roomsTriple")
          : translateService.instant("dictionary.roomTriple");
        break;
      case EnvironmentCategory.FAMILY_ROOM:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.familyRooms")
          : translateService.instant("dictionary.familyRoom");
        break;
      case EnvironmentCategory.DORMITORY:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.dormitories")
          : translateService.instant("dictionary.dormitory");
        break;
      case EnvironmentCategory.COMMON_AREA:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.commonAreas")
          : translateService.instant("dictionary.commonArea");
        break;
      case EnvironmentCategory.ELAVATOR_FLOOR:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.elavatorFloors")
          : translateService.instant("dictionary.elevatorFloor");
        break;
      case EnvironmentCategory.CABINETS:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.cabinets")
          : translateService.instant("dictionary.cabinet");
        break;
      case EnvironmentCategory.SKI_DEPOSITS:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.skiDepot")
          : translateService.instant("dictionary.skiDepot");
        break;
      case EnvironmentCategory.EXTERNAL_ACCESS:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.externalAccesses")
          : translateService.instant("dictionary.extranalAccess");
        break;
      case EnvironmentCategory.FLOOR:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.floors")
          : translateService.instant("dictionary.floor");
        break;
      case EnvironmentCategory.APARTAMENT:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.apartments")
          : translateService.instant("dictionary.apartment");
        break;
      case EnvironmentCategory.GARAGE:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.garages")
          : translateService.instant("dictionary.garage");
        break;
      case EnvironmentCategory.STAIRS:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.stairs")
          : translateService.instant("dictionary.stairs");
        break;
      case EnvironmentCategory.CONDOMINIUM:
        environmentCategoryString = pluralString
          ? translateService.instant("dictionary.condominiumsBuilding")
          : translateService.instant("dictionary.condominiumBuilding");
        break;
      default:
        environmentCategoryString = translateService.instant(
          "dictionary.notDefined"
        );
        break;
    }

    return environmentCategoryString;
  }
  //#endregion

  //#region Settings
  /**
   * This method handle both the association between each "RegisteredAppLanguage" enum's value and a user-friendly string AND the localisation of these strings.
   * @param registeredLanguage The enum value to be associated with a user-friendly, localised string
   * @param translateService Ref. to TranslateService, handling the string localistaion.
   * @returns A user-friendly, localised string.
   */
  public static registeredAppLanguageEnumAsString(
    registeredLanguage: RegisteredAppLanguage,
    translateService: TranslateService
  ): string {

    let check: any;
    if (RegisteredAppLanguage[registeredLanguage] == undefined) {
      check = registeredLanguage;
    } else {
      check = RegisteredAppLanguage[registeredLanguage];
    }

    let returnValue: string = "";
    switch (check) {
      //switch (registeredLanguage) {
      case RegisteredAppLanguage.ITA:
        returnValue = translateService.instant("dictionary.languageIta");
        break;

      case RegisteredAppLanguage.ENG:
        returnValue = translateService.instant("dictionary.languageEng");
        break;

      case RegisteredAppLanguage.FRA:
        returnValue = translateService.instant("dictionary.languageFr");
        break;

      case RegisteredAppLanguage.ESP:
        returnValue = translateService.instant("dictionary.languageEs");
        break;

      case RegisteredAppLanguage.DEU:
        returnValue = translateService.instant("dictionary.languageDe");
        break;

      default:
        returnValue = translateService.instant("dictionary.unknow");
        break;
    }

    return returnValue;
  }
  //#endregion
}
